'use client';

import { useMemo } from 'react';
import { useLocale, useTranslations } from 'next-intl';
import { useQuery } from '@tanstack/react-query';
import {
  AlertTriangle,
  CheckCircle,
  ChevronDown,
  Clock,
  History,
  Loader2,
  ScanText
} from 'lucide-react';

import { cn } from '@/lib/utils';
import { formatDate } from '@/lib/format';
import { Badge } from '@/components/ui/badge';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle
} from '@/components/ui/sheet';
import { Separator } from '@/components/ui/separator';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger
} from '@/components/ui/collapsible';
import { ScrollArea } from '@/components/ui/scroll-area';
import { ocrQueries } from '../services/ocr.queries';
import { resolveFieldLabel } from '../utils/field-label';
import type { ExtractedDataItem, OcrResult } from '../types';

// One archived OCR run, grouped by the `archived_at` timestamp the backend
// stamps when results are superseded on a repair return. All sibling results
// archived together share the same timestamp, so they collapse into one run.
interface ArchivedRun {
  /** Stable key + grouping value — the raw archived_at ISO string. */
  archivedAt: string;
  /** Reason recorded for the archival (first non-null wins). */
  archiveReason: string | null;
  results: OcrResult[];
}

function groupByRun(results: OcrResult[]): ArchivedRun[] {
  const byTimestamp = new Map<string, ArchivedRun>();

  for (const result of results) {
    // Defensive: history results always carry archived_at, but fall back to
    // created_at so a stray active result never silently drops out.
    const archivedAt = result.archived_at ?? result.created_at;
    const existing = byTimestamp.get(archivedAt);
    if (existing) {
      existing.results.push(result);
      if (!existing.archiveReason && result.archive_reason) {
        existing.archiveReason = result.archive_reason;
      }
    } else {
      byTimestamp.set(archivedAt, {
        archivedAt,
        archiveReason: result.archive_reason ?? null,
        results: [result]
      });
    }
  }

  // Newest run first.
  return Array.from(byTimestamp.values()).sort(
    (a, b) =>
      new Date(b.archivedAt).getTime() - new Date(a.archivedAt).getTime()
  );
}

function ConfidenceDot({ rate }: { rate: number }) {
  const color =
    rate >= 0.95
      ? 'bg-emerald-500'
      : rate >= 0.8
        ? 'bg-blue-500'
        : 'bg-red-500';
  return (
    <span className={cn('inline-block h-1.5 w-1.5 rounded-full', color)} />
  );
}

// Read-only mirror of container-results-sheet's FieldQuickView — same markup,
// no accept/edit affordances (history is immutable).
function ReadOnlyFields({ fields }: { fields: ExtractedDataItem[] }) {
  const t = useTranslations('ocr');
  const locale = useLocale();

  if (fields.length === 0) {
    return (
      <p className='text-muted-foreground px-3 py-2 text-xs italic'>
        {t('noExtractedData')}
      </p>
    );
  }

  return (
    <div className='bg-muted/40 mt-2 rounded-md border'>
      <div className='divide-y'>
        {fields.map((field) => {
          const confidence = field.confidence_rate ?? 0;
          const isLow = confidence < 0.8;
          const label = resolveFieldLabel(field, locale);

          return (
            <div
              key={`${field.key}-${field.page_number ?? ''}`}
              className='flex items-start gap-2 px-3 py-2'
            >
              <div className='min-w-0 flex-1'>
                <div className='flex items-center gap-1.5'>
                  <span className='text-muted-foreground truncate text-[11px] font-medium uppercase'>
                    {label}
                  </span>
                  <ConfidenceDot rate={confidence} />
                  {isLow && (
                    <AlertTriangle className='h-3 w-3 shrink-0 text-red-500' />
                  )}
                </div>
                {/* `dir='auto'` — an archived value is in the document's
                    language; inherited RTL reorders its numeric runs. */}
                <p dir='auto' className='mt-0.5 truncate text-sm'>
                  {field.value != null ? (
                    String(field.value)
                  ) : (
                    <span className='text-muted-foreground italic'>
                      {t('noValue')}
                    </span>
                  )}
                </p>
              </div>
              {field.status === 'processed' ? (
                <CheckCircle className='mt-1 h-3.5 w-3.5 shrink-0 text-emerald-500' />
              ) : (
                <Clock className='text-muted-foreground mt-1 h-3.5 w-3.5 shrink-0' />
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

interface OcrHistorySheetProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  containerId: number;
  containerLabel?: string;
}

export function OcrHistorySheet({
  open,
  onOpenChange,
  containerId,
  containerLabel
}: OcrHistorySheetProps) {
  const t = useTranslations('ocr');
  const locale = useLocale();

  // Only fetch while the sheet is open — history is an on-demand view.
  const { data, isPending } = useQuery({
    ...ocrQueries.containerHistory(containerId),
    enabled: open && containerId > 0
  });

  const runs = useMemo(
    () => groupByRun((data?.data ?? []) as OcrResult[]),
    [data]
  );

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent className='flex w-full flex-col gap-0 p-0 sm:max-w-md'>
        <SheetHeader className='space-y-2 border-b px-5 py-4'>
          <SheetTitle className='flex items-center gap-2 text-base'>
            <History className='h-4 w-4' />
            {t('history')}
            <Badge variant='secondary' className='text-[10px] font-normal'>
              {t('readOnly')}
            </Badge>
          </SheetTitle>
          <SheetDescription className='text-xs'>
            {containerLabel ? `${containerLabel} — ` : ''}
            {t('historyDescription')}
          </SheetDescription>
        </SheetHeader>

        <ScrollArea className='flex-1 overflow-auto'>
          {isPending ? (
            <div className='flex flex-col items-center justify-center gap-3 py-16'>
              <Loader2 className='text-muted-foreground h-6 w-6 animate-spin' />
              <p className='text-muted-foreground text-xs'>
                {t('loadingPreviousResults')}
              </p>
            </div>
          ) : runs.length === 0 ? (
            <div className='flex flex-col items-center justify-center px-6 py-16 text-center'>
              <div className='bg-muted mb-4 rounded-full p-3'>
                <ScanText className='text-muted-foreground h-8 w-8' />
              </div>
              <p className='text-muted-foreground text-sm font-medium'>
                {t('noPreviousResults')}
              </p>
            </div>
          ) : (
            <div className='divide-y'>
              {runs.map((run, index) => {
                const formattedDate = formatDate(
                  run.archivedAt,
                  {
                    year: 'numeric',
                    month: 'short',
                    day: 'numeric',
                    hour: '2-digit',
                    minute: '2-digit'
                  },
                  locale
                );

                return (
                  <Collapsible
                    key={run.archivedAt}
                    // Newest run starts expanded; older runs collapsed.
                    defaultOpen={index === 0}
                  >
                    <CollapsibleTrigger className='hover:bg-muted/50 group flex w-full items-center gap-2 px-5 py-3 text-start transition-colors'>
                      <ChevronDown className='text-muted-foreground h-4 w-4 shrink-0 transition-transform group-data-[state=open]:rotate-180' />
                      <div className='min-w-0 flex-1'>
                        <p className='truncate text-sm font-medium'>
                          {t('runFrom', { date: formattedDate })}
                        </p>
                        {run.archiveReason && (
                          <p className='text-muted-foreground truncate text-xs'>
                            {t('archiveReason', { reason: run.archiveReason })}
                          </p>
                        )}
                      </div>
                      <Badge variant='outline' className='text-[10px]'>
                        {run.results.length}
                      </Badge>
                    </CollapsibleTrigger>

                    <CollapsibleContent>
                      <div className='space-y-3 px-5 pb-4'>
                        {run.results.map((result, resultIndex) => {
                          const pageLabel = result.page_number
                            ? `${t('pages')} ${result.page_number}`
                            : `${t('pages')} ${resultIndex + 1}`;
                          const templateName = result.ocr_template?.name;

                          return (
                            <div key={result.id}>
                              <div className='flex items-center justify-between gap-2'>
                                <span className='text-xs font-medium'>
                                  {pageLabel}
                                </span>
                                {templateName && (
                                  <Badge
                                    variant='outline'
                                    className='max-w-[55%] truncate text-[10px]'
                                  >
                                    {templateName}
                                  </Badge>
                                )}
                              </div>
                              <ReadOnlyFields
                                fields={result.extracted_data ?? []}
                              />
                            </div>
                          );
                        })}
                      </div>
                      <Separator />
                    </CollapsibleContent>
                  </Collapsible>
                );
              })}
            </div>
          )}
        </ScrollArea>
      </SheetContent>
    </Sheet>
  );
}
